/**
 * What a session's agent is given **besides its own config** (§5.8, §5.26): the
 * MCP servers it may call, the skills it may use, and the plugins it loads.
 *
 * It is one setting and not three because it is one decision — *what this work
 * needs the agent to be able to do* — and because all three answer it the same
 * way: a named thing, on or off, that the provisioner turns into a line of the
 * agent's own configuration inside the guest. Each agent's own keys are where
 * that lands (`enabledPlugins`, `mcpServers`, `~/.claude/skills`), so nothing
 * here is a second mechanism: this is the *list*, and the agent's config is
 * where it is written.
 *
 * **Overridable per workspace** (§4.1), which is the whole point of it being a
 * setting rather than a page. A personal install answers it once in the
 * application settings and may differ per workspace; an organization answers it
 * for a workspace it shares, and the document carries it up on a publish and
 * back down on a join exactly as it carries the model or the VM's memory —
 * nothing extra is written for that, because `parseProjectDocument` reads this
 * validator (§5.25).
 *
 * **No credential crosses in it.** A value that reads as a secret is refused
 * here, by name, for the reason an organization's settings refuse one: this
 * document is published to an organization, pulled onto every member's machine
 * and shown in a browser. A server that needs a token reads it from the
 * workspace's own secrets, which are sealed and never travel in the clear.
 */
import { looksSecret } from '../workspace/secrets';

/** One MCP server a session's agent may call. */
export interface McpServerEntry {
  /** What the agent knows it by — the key in its own config. */
  name: string;
  /** The command that starts it **inside the session**, on the guest's PATH. */
  command: string;
  args: string[];
  /** Plain environment for it. A credential belongs in the workspace's secrets. */
  env: { name: string; value: string }[];
  enabled: boolean;
}

/**
 * One skill a session's agent is given.
 *
 * A **path in the session's own workspace** first: the repositories are cloned
 * into the guest, so a skill a team keeps in one of them is already there —
 * what it is missing is being seen from the workspace root, which is where the
 * agent runs.
 *
 * Or the address of a **public GitHub repository** — or of a folder of one —
 * which the host downloads and copies in at boot (the owner's ask, 2026-09-30:
 * a skill published on GitHub put here was silently never there). The guest
 * fetches nothing, so the sandbox's allowlist is not widened for it; what is
 * trusted is the address somebody wrote into these settings, as a plugin's
 * marketplace already is.
 */
export interface SkillEntry {
  /** The name the agent lists it under. */
  name: string;
  /** Where it lives: relative to the session's workspace root, or `https://github.com/owner/repo[/tree/ref/folder]`. */
  path: string;
  enabled: boolean;
}

/** A GitHub repository, and optionally a branch or tag and a folder of it, as the browser's address bar shows one. */
const GITHUB_SKILL =
  /^https:\/\/github\.com\/([A-Za-z0-9_.-]+)\/([A-Za-z0-9_.-]+?)(?:\.git)?(?:\/tree\/([A-Za-z0-9_.-]+)(?:\/([A-Za-z0-9_./-]+?))?)?\/?$/;

export interface GithubSkill {
  owner: string;
  repo: string;
  /** A branch or tag; null is the repository's default. */
  ref: string | null;
  /** A folder of the repository to take the skill from; null is wherever its `SKILL.md` files are. */
  folder: string | null;
}

/**
 * A skill's path read as a GitHub address, or null when it is a workspace path.
 * `/tree/<ref>/<folder>` is split at the first slash after the ref: a branch
 * whose name has a slash in it cannot be named here, and the default branch is
 * what an address with no ref takes.
 */
export function githubSkillOf(path: string): GithubSkill | null {
  const trimmed = path.trim();
  if (trimmed.split('/').includes('..')) return null;
  const match = GITHUB_SKILL.exec(trimmed);
  if (!match) return null;
  const folder = match[4] ?? null;
  return { owner: match[1], repo: match[2], ref: match[3] ?? null, folder };
}

/** One plugin a session's agent loads, and the marketplace it comes from. */
export interface PluginEntry {
  name: string;
  /** The marketplace's name, as the agent registers it. */
  marketplace: string;
  /** Where that marketplace is — `owner/repo`, or an https URL. */
  source: string;
  enabled: boolean;
}

export interface AgentExtensions {
  mcpServers: McpServerEntry[];
  skills: SkillEntry[];
  plugins: PluginEntry[];
}

export const EMPTY_EXTENSIONS: AgentExtensions = { mcpServers: [], skills: [], plugins: [] };

/**
 * Enough for the work, few enough that the list is still read. Each of these
 * costs a session something real — a server is a process in the guest, a plugin
 * is a download — so the caps are a budget rather than a formality.
 */
export const MAX_MCP_SERVERS = 20;
export const MAX_SKILLS = 40;
export const MAX_PLUGINS = 20;
const MAX_ARGS = 24;
const MAX_ENV = 24;

/** What the agents accept as a name for one of these, and what a path may be. */
export const EXTENSION_NAME = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;
/** `owner/repo`, or an https URL — the two a marketplace is named by. */
export const MARKETPLACE_SOURCE = /^(https:\/\/[^\s]{4,300}|[A-Za-z0-9._-]{1,80}\/[A-Za-z0-9._-]{1,80})$/;
const ENV_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;

const text = (value: unknown, max: number): string => (typeof value === 'string' ? value.trim().slice(0, max) : '');

/** A refusal and the field of the entry it is about, so an editor marks that one. */
export interface ExtensionProblem<F extends string> {
  field: F;
  message: string;
}

/**
 * Why this entry cannot be kept, or `null`. The editors call it so a refusal is
 * a sentence beside the field that caused it rather than a row that vanishes on
 * the next read — the same bargain the egress list already makes.
 */
export function mcpProblemAt(
  entry: Partial<McpServerEntry>
): ExtensionProblem<'name' | 'command' | `env.${number}`> | null {
  if (!EXTENSION_NAME.test(text(entry.name, 64))) {
    return {
      field: 'name',
      message: 'A server is named with letters, digits, dot, dash or underscore — the name the agent calls it by.'
    };
  }
  if (!text(entry.command, 400)) {
    return {
      field: 'command',
      message: 'Name the command that starts it inside the session — such as npx @playwright/mcp.'
    };
  }
  for (const [at, pair] of (entry.env ?? []).entries()) {
    if (!ENV_NAME.test(text(pair.name, 120))) {
      return { field: `env.${at}`, message: `“${pair.name}” is not the name of an environment variable.` };
    }
    // The same refusal an organization's settings make, for the same reason:
    // this document is published, pulled onto every machine, and readable in a
    // browser. A server that needs a token reads it from the workspace secrets.
    if (looksSecret(pair.name, pair.value)) {
      return {
        field: `env.${at}`,
        message: `${pair.name} looks like a credential. Put it in the workspace's secrets and name it there — this list is published.`
      };
    }
  }
  return null;
}

export function skillProblemAt(entry: Partial<SkillEntry>): ExtensionProblem<'name' | 'path'> | null {
  if (!EXTENSION_NAME.test(text(entry.name, 64))) {
    return {
      field: 'name',
      message: 'A skill is named with letters, digits, dot, dash or underscore — such as review.'
    };
  }
  const path = text(entry.path, 300);
  if (!path) {
    return {
      field: 'path',
      message: 'Say where it lives, relative to the workspace root — such as tools/skills/review.'
    };
  }
  if (/^[a-z]+:\/\//i.test(path)) {
    return githubSkillOf(path)
      ? null
      : {
          field: 'path',
          message:
            'An address is fetched from GitHub only — such as https://github.com/acme/skills, or a folder of one.'
        };
  }
  if (path.startsWith('/') || path.split('/').includes('..')) {
    return {
      field: 'path',
      message: 'A skill lives inside the session’s workspace — a relative path, with no “..” in it.'
    };
  }
  return null;
}

export function pluginProblemAt(
  entry: Partial<PluginEntry>
): ExtensionProblem<'name' | 'marketplace' | 'source'> | null {
  if (!EXTENSION_NAME.test(text(entry.name, 64))) {
    return { field: 'name', message: 'A plugin is named as its marketplace names it — such as formatter.' };
  }
  if (!EXTENSION_NAME.test(text(entry.marketplace, 64))) {
    return { field: 'marketplace', message: 'Name the marketplace it comes from — such as acme-tools.' };
  }
  if (!MARKETPLACE_SOURCE.test(text(entry.source, 300))) {
    return {
      field: 'source',
      message: 'A marketplace is an owner/repo or an https URL — such as acme/agent-plugins.'
    };
  }
  return null;
}

/** The same refusals as sentences alone — what the stores and the Worker ask. */
export function mcpProblem(entry: Partial<McpServerEntry>): string | null {
  return mcpProblemAt(entry)?.message ?? null;
}

export function skillProblem(entry: Partial<SkillEntry>): string | null {
  return skillProblemAt(entry)?.message ?? null;
}

export function pluginProblem(entry: Partial<PluginEntry>): string | null {
  return pluginProblemAt(entry)?.message ?? null;
}

/**
 * The stored value as this app reads it — everything that does not survive is
 * **dropped**, never repaired. A server whose command was lost is not a server
 * to start with half an answer, and a page that showed a repaired row would be
 * promising a session something nobody wrote.
 */
export function normalizeExtensions(input: unknown): AgentExtensions {
  const doc = (input ?? {}) as Partial<AgentExtensions>;
  return {
    mcpServers: (Array.isArray(doc.mcpServers) ? doc.mcpServers : [])
      .map((raw) => {
        const entry = (raw ?? {}) as Partial<McpServerEntry>;
        return {
          name: text(entry.name, 64),
          command: text(entry.command, 400),
          args: (Array.isArray(entry.args) ? entry.args : []).slice(0, MAX_ARGS).map((arg) => text(arg, 400)),
          env: (Array.isArray(entry.env) ? entry.env : [])
            .slice(0, MAX_ENV)
            .map((pair) => ({ name: text(pair?.name, 120), value: text(pair?.value, 2000) })),
          enabled: entry.enabled !== false
        };
      })
      .filter((entry) => mcpProblem(entry) === null)
      .slice(0, MAX_MCP_SERVERS),
    skills: (Array.isArray(doc.skills) ? doc.skills : [])
      .map((raw) => {
        const entry = (raw ?? {}) as Partial<SkillEntry>;
        return { name: text(entry.name, 64), path: text(entry.path, 300), enabled: entry.enabled !== false };
      })
      .filter((entry) => skillProblem(entry) === null)
      .slice(0, MAX_SKILLS),
    plugins: (Array.isArray(doc.plugins) ? doc.plugins : [])
      .map((raw) => {
        const entry = (raw ?? {}) as Partial<PluginEntry>;
        return {
          name: text(entry.name, 64),
          marketplace: text(entry.marketplace, 64),
          source: text(entry.source, 300),
          enabled: entry.enabled !== false
        };
      })
      .filter((entry) => pluginProblem(entry) === null)
      .slice(0, MAX_PLUGINS)
  };
}

/** How many of each are actually on, for a row that has to say so in a few words. */
export function extensionsSummary(value: AgentExtensions): string {
  const on = (list: { enabled: boolean }[]): number => list.filter((entry) => entry.enabled).length;
  const parts = [
    [on(value.skills), 'skill'],
    [on(value.mcpServers), 'MCP server'],
    [on(value.plugins), 'plugin']
  ] as const;
  const said = parts
    .filter(([count]) => count > 0)
    .map(([count, word]) => `${count} ${word}${count === 1 ? '' : 's'}`);
  return said.length ? said.join(' · ') : 'nothing added';
}
